import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/apiService';
import { saveNavContext } from './reclamacoesNav';
import './Reclamacoes.css';

/**
 * Reclamações (VAR + dislike no feedback) — fila.
 *
 * Sem empresa escolhida, a tela abre como fila: as empresas com pendências, minimizadas, com a
 * contagem; ao expandir, os itens daquela empresa (mais antigo primeiro).
 * Com empresa escolhida, mostra direto a lista dela, com loja e vendedor.
 * "Revisar" abre a revisão em etapas (/reclamacoes-pontos/:subdomain/:transcriptionId/perguntas).
 *
 * Dois tipos de item:
 *  - VAR: reclamação de pontuação por comportamento (score_disputes);
 *  - dislike: 👎 no feedback SEM VAR — reclamação sobre o feedback, não sobre pontos.
 *
 * Período padrão: últimos 30 dias (data da reclamação no VAR, do feedback no dislike), na lista e
 * na contagem por empresa; limpar as datas mostra tudo.
 * Filtros, empresas expandidas e rolagem ficam no sessionStorage: voltar da revisão devolve a
 * tela como estava. Ao abrir um item, a lista visível é guardada (reclamacoesNav) para o
 * "Enviar e abrir a próxima" da revisão.
 * Deep link do alerta de dislike da Home: ?subdomain=&store_id=&salesman_id=&feedback_ids= —
 * se sobrar um único pendente, abre direto nele.
 */

const STATUS_LABEL = {
    pending: 'Pendente', granted: 'Concedida', denied: 'Negada', partial: 'Parcial', answered: 'Respondida',
};
const BEH_STATUS_CLASS = {
    claimed_pending: 'vr-chip-pending', claimed_granted: 'vr-chip-granted', claimed_denied: 'vr-chip-denied',
};
const STATE_KEY = 'vr-list-state:v2';
const DEFAULT_DAYS = 30;
// data pura em São Paulo (YYYY-MM-DD), sem passar por meia-noite UTC
const brIsoDate = (ms) => new Date(ms).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
const defaultPeriod = () => ({
    dateStart: brIsoDate(Date.now() - DEFAULT_DAYS * 86400000), dateEnd: brIsoDate(Date.now()),
});
const DEFAULTS = {
    subdomain: '', storeId: '', salesmanId: '', dateStart: '', dateEnd: '',
    status: 'pending', order: 'oldest', kind: '', feedbackIds: '', open: {}, scroll: 0,
};

function readState() {
    try { return { ...DEFAULTS, ...defaultPeriod(), ...(JSON.parse(sessionStorage.getItem(STATE_KEY) || '{}')) }; } catch (e) { return { ...DEFAULTS, ...defaultPeriod() }; }
}
function writeState(st) {
    try { sessionStorage.setItem(STATE_KEY, JSON.stringify(st)); } catch (e) { /* ignore */ }
}

// created/attendance_at vêm em UTC (naive) -> dd/mm/aaaa HH:MM em São Paulo
const brDateTime = (iso) => {
    if (!iso) return '—';
    const s = String(iso);
    const dt = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : s + 'Z');
    if (isNaN(dt)) return s;
    return dt.toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo',
    });
};
const intNum = (v) => (v === null || v === undefined ? '—' : Math.round(Number(v)).toLocaleString('pt-BR'));

function KindChip({ kind }) {
    return kind === 'dislike'
        ? <span className="vr-chip vr-chip-dislike" title="👎 no feedback, sem reclamação de pontos (VAR)">👎 Feedback</span>
        : <span className="vr-chip vr-chip-var" title="Reclamação de pontuação por comportamento">VAR</span>;
}

function DisputeTable({ rows, subdomain, onOpen }) {
    if (!rows) return <div className="vr-empty">Carregando…</div>;
    if (rows.length === 0) return <div className="vr-empty">Nenhum item com esses filtros.</div>;
    return (
        <table className="vr-table">
            <thead><tr>
                <th>Tipo</th><th>Reclamada em</th><th>Atendimento</th><th>Vendedor</th><th>Loja</th>
                <th>O que foi reclamado</th><th className="vr-num">Pontos em jogo</th><th>Situação</th><th></th>
            </tr></thead>
            <tbody>
                {rows.map((d) => (
                    <tr key={`${d.kind}-${d.transcription_id}`}>
                        <td><KindChip kind={d.kind} /></td>
                        <td>{brDateTime(d.created)}{d.source === 'backfill' ? <div className="vr-muted vr-small">histórico</div> : null}</td>
                        <td>{brDateTime(d.attendance_at)}</td>
                        <td>{d.salesman_name || '—'}</td>
                        <td className="vr-muted">{d.store_name || '—'}</td>
                        <td>
                            {d.kind === 'dislike' ? (
                                <div className="vr-small">
                                    <div>Reclamação sobre o feedback</div>
                                    {(d.comment || '').trim() && <div className="vr-muted">“{d.comment.trim().slice(0, 80)}”</div>}
                                </div>
                            ) : (
                                <div className="vr-chips">
                                    {(d.behaviors || []).map((b) => (
                                        <span key={b.behavior_key || b.raw_label} className={`vr-chip ${BEH_STATUS_CLASS[b.status] || ''}`}>
                                            {b.display_name || b.raw_label}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </td>
                        <td className="vr-num">{d.kind === 'dislike' ? '—' : intNum(d.claimed_open_points)}</td>
                        <td><span className={`vr-chip vr-chip-${d.status || 'pending'}`}>{STATUS_LABEL[d.status] || d.status || '—'}</span></td>
                        <td className="vr-num">
                            <button className="vr-btn vr-btn-sm vr-btn-primary" onClick={() => onOpen(subdomain, d.transcription_id, rows)}>
                                {d.is_reviewed ? 'Ver' : 'Revisar'} ›
                            </button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function Reclamacoes({ user }) {
    const userId = user?.user_id;
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [toast, setToast] = useState(null);
    const notify = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    // Estado inicial: o que estava na sessão, sobrescrito pelo deep link (alerta da Home).
    const initial = useRef(null);
    if (initial.current === null) {
        const st = readState();
        const fromLink = searchParams.get('feedback_ids') || searchParams.get('subdomain');
        if (fromLink) {
            Object.assign(st, {
                subdomain: searchParams.get('subdomain') || '', storeId: searchParams.get('store_id') || '',
                salesmanId: searchParams.get('salesman_id') || '', feedbackIds: searchParams.get('feedback_ids') || '',
                dateStart: '', dateEnd: '', status: 'pending', kind: '', scroll: 0,
            });
        }
        st.autoOpen = !!searchParams.get('feedback_ids');
        initial.current = st;
    }
    const init = initial.current;

    const [companies, setCompanies] = useState([]);
    const [stores, setStores] = useState([]);
    const [salesmen, setSalesmen] = useState([]);
    const [subdomain, setSubdomain] = useState(init.subdomain);
    const [storeId, setStoreId] = useState(init.storeId);
    const [salesmanId, setSalesmanId] = useState(init.salesmanId);
    const [dateStart, setDateStart] = useState(init.dateStart);
    const [dateEnd, setDateEnd] = useState(init.dateEnd);
    const [status, setStatus] = useState(init.status);
    const [order, setOrder] = useState(init.order);
    const [kind, setKind] = useState(init.kind);
    const [feedbackIds, setFeedbackIds] = useState(init.feedbackIds);

    const [overview, setOverview] = useState(null);       // [{subdomain, name, pending, pending_disputes, pending_dislikes, oldest_pending_at}]
    const [open, setOpen] = useState(init.open || {});     // subdomain -> bool
    const [rowsBySd, setRowsBySd] = useState({});          // subdomain -> rows | null (carregando)
    const [loading, setLoading] = useState(false);
    const autoOpenRef = useRef(init.autoOpen);
    const scrollRef = useRef(init.scroll || 0);
    const rootRef = useRef(null);

    // tira o deep link da URL (o estado já foi lido): voltar da revisão não reaplica o alerta
    useEffect(() => {
        if ([...searchParams.keys()].length) setSearchParams({}, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        writeState({ subdomain, storeId, salesmanId, dateStart, dateEnd, status, order, kind, feedbackIds, open, scroll: scrollRef.current });
    }, [subdomain, storeId, salesmanId, dateStart, dateEnd, status, order, kind, feedbackIds, open]);

    useEffect(() => {
        (async () => {
            try {
                const data = await apiService.promptEditorGetCompanies();
                setCompanies(Array.isArray(data) ? data : []);
            } catch (e) { notify('Erro ao carregar empresas', 'error'); }
        })();
    }, [notify]);

    // opções dependentes: só carregam; quem limpa loja/vendedor é a troca feita pela pessoa
    // (assim o estado restaurado da sessão não é apagado ao montar)
    useEffect(() => {
        setStores([]);
        if (!subdomain) return;
        (async () => {
            try {
                const st = await apiService.promptEditorGetStores(subdomain);
                setStores(Array.isArray(st) ? st : []);
            } catch (e) { notify('Erro ao carregar lojas', 'error'); }
        })();
    }, [subdomain, notify]);

    useEffect(() => {
        setSalesmen([]);
        if (!subdomain || !storeId) return;
        (async () => {
            try {
                const r = await apiService.pointsSalesmen({ userId, subdomain, store_id: storeId });
                setSalesmen(r.salesmen || []);
            } catch (e) { /* silencioso */ }
        })();
    }, [subdomain, storeId, userId]);

    const onSubdomain = (v) => { setSubdomain(v); setStoreId(''); setSalesmanId(''); setFeedbackIds(''); };
    const onStore = (v) => { setStoreId(v); setSalesmanId(''); };

    const listParams = useCallback((sd) => ({
        subdomain: sd,
        store_id: sd === subdomain && storeId ? storeId : undefined,
        salesman_id: sd === subdomain && salesmanId ? salesmanId : undefined,
        date_start: dateStart || undefined, date_end: dateEnd || undefined,
        include_before_activation: true,
        status: status === 'all' ? undefined : status, order,
        kind: kind || undefined,
        feedback_ids: sd === subdomain && feedbackIds ? feedbackIds.split(',').map(Number).filter(Boolean) : undefined,
    }), [subdomain, storeId, salesmanId, dateStart, dateEnd, status, order, kind, feedbackIds]);

    const restoreScroll = () => {
        const sc = rootRef.current && rootRef.current.closest('.app-main');
        if (sc && scrollRef.current) setTimeout(() => { sc.scrollTop = scrollRef.current; }, 0);
    };

    const loadList = useCallback(async (sd) => {
        setRowsBySd((p) => ({ ...p, [sd]: null }));
        try {
            const params = listParams(sd);
            const r = await apiService.pointsDisputesList({ userId, ...params });
            if (!r || !Array.isArray(r.disputes)) throw new Error('resposta inesperada da lista');
            setRowsBySd((p) => ({ ...p, [sd]: r.disputes }));
            // alerta da Home: um único pendente -> abre direto nele
            if (autoOpenRef.current) {
                autoOpenRef.current = false;
                const pend = r.disputes.filter((d) => !d.is_reviewed);
                if (pend.length === 1) {
                    saveNavContext({ subdomain: sd, params, tids: r.disputes.map((d) => d.transcription_id) });
                    navigate(`/reclamacoes-pontos/${encodeURIComponent(sd)}/${pend[0].transcription_id}/perguntas`);
                    return;
                }
            }
            restoreScroll();
        } catch (e) {
            setRowsBySd((p) => ({ ...p, [sd]: [] }));
            notify(e.response?.data?.detail || e.message || 'Erro ao carregar reclamações', 'error');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, listParams, notify, navigate]);

    const loadOverview = useCallback(async (reopen) => {
        setLoading(true);
        try {
            const r = await apiService.pointsDisputesOverview({
                userId, limit: 20, date_start: dateStart || undefined, date_end: dateEnd || undefined,
            });
            if (!r || !Array.isArray(r.companies)) throw new Error('resposta inesperada da fila');
            setOverview(r.companies);
            // empresas que estavam expandidas antes de abrir uma revisão
            (reopen ? Object.keys(open).filter((sd) => open[sd]) : []).forEach((sd) => loadList(sd));
            restoreScroll();
        } catch (e) {
            notify(e.response?.data?.detail || e.message || 'Erro ao carregar a fila', 'error');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, notify, loadList, dateStart, dateEnd]);

    // Abre: empresa escolhida -> lista dela; senão a fila por empresa (com as expansões de antes)
    useEffect(() => {
        if (!userId) return;
        if (subdomain) loadList(subdomain); else loadOverview(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const buscar = () => {
        scrollRef.current = 0;
        setRowsBySd({});
        if (subdomain) { loadList(subdomain); return; }
        setOpen({});
        loadOverview(false);
    };

    const toggleCompany = (sd) => {
        const willOpen = !open[sd];
        setOpen((p) => ({ ...p, [sd]: willOpen }));
        if (willOpen && rowsBySd[sd] === undefined) loadList(sd);
    };

    // trocar de empresa no filtro: carrega a lista dela (a 1ª carga é a do efeito acima)
    const firstSd = useRef(true);
    useEffect(() => {
        if (firstSd.current) { firstSd.current = false; return; }
        if (subdomain) loadList(subdomain); else loadOverview(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subdomain]);

    // "Ver todos" do filtro do alerta: recarrega com os filtros já sem os feedback_ids
    const firstFb = useRef(true);
    useEffect(() => {
        if (firstFb.current) { firstFb.current = false; return; }
        if (subdomain && !feedbackIds) loadList(subdomain);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feedbackIds]);

    const openCase = (sd, tid, rows) => {
        const sc = rootRef.current && rootRef.current.closest('.app-main');
        scrollRef.current = sc ? sc.scrollTop : 0;
        writeState({ subdomain, storeId, salesmanId, dateStart, dateEnd, status, order, kind, feedbackIds, open, scroll: scrollRef.current });
        saveNavContext({ subdomain: sd, params: listParams(sd), tids: (rows || []).map((d) => d.transcription_id) });
        navigate(`/reclamacoes-pontos/${encodeURIComponent(sd)}/${tid}/perguntas`);
    };
    const companyName = (sd) => (companies.find((c) => c.subdomain === sd) || {}).name || sd;

    return (
        <div className="vr-page" ref={rootRef}>
            {toast && <div className={`vr-toast ${toast.type}`}>{toast.msg}</div>}
            <div className="vr-list">
                <h1 className="vr-title">Reclamações</h1>
                <p className="vr-subtitle">
                    Reclamações de pontuação (VAR) e dislikes no feedback aguardando revisão. Abra uma para revisar pergunta a pergunta e responder ao vendedor.
                    Por padrão, os últimos {DEFAULT_DAYS} dias — limpe as datas para ver tudo.
                </p>

                <div className="vr-filters">
                    <div className="vr-field">
                        <label>Empresa</label>
                        <select value={subdomain} onChange={(e) => onSubdomain(e.target.value)}>
                            <option value="">Todas (fila por empresa)</option>
                            {companies.map((c) => <option key={c.subdomain} value={c.subdomain}>{c.name || c.subdomain}</option>)}
                        </select>
                    </div>
                    <div className="vr-field">
                        <label>Loja</label>
                        <select value={storeId} onChange={(e) => onStore(e.target.value)} disabled={!subdomain}>
                            <option value="">Todas</option>
                            {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                        </select>
                    </div>
                    <div className="vr-field">
                        <label>Vendedor</label>
                        <select value={salesmanId} onChange={(e) => setSalesmanId(e.target.value)} disabled={!storeId}>
                            <option value="">{storeId ? 'Todos' : 'Escolha a loja'}</option>
                            {salesmen.map((s) => <option key={s.salesman_id} value={s.salesman_id}>{s.salesman_name}</option>)}
                        </select>
                    </div>
                    <div className="vr-field">
                        <label>Tipo</label>
                        <select value={kind} onChange={(e) => setKind(e.target.value)}>
                            <option value="">VAR e dislikes</option>
                            <option value="dispute">Só VAR (pontuação)</option>
                            <option value="dislike">Só dislike no feedback</option>
                        </select>
                    </div>
                    <div className="vr-field">
                        <label>De</label>
                        <input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} />
                    </div>
                    <div className="vr-field">
                        <label>Até</label>
                        <input type="date" value={dateEnd} min={dateStart || undefined} onChange={(e) => setDateEnd(e.target.value)} />
                    </div>
                    <div className="vr-field">
                        <label>Situação</label>
                        <select value={status} onChange={(e) => setStatus(e.target.value)}>
                            <option value="pending">Pendentes</option>
                            <option value="granted">Concedidas (VAR)</option>
                            <option value="partial">Parciais (VAR)</option>
                            <option value="denied">Negadas (VAR)</option>
                            <option value="answered">Respondidos (dislike)</option>
                            <option value="all">Todas</option>
                        </select>
                    </div>
                    <div className="vr-field">
                        <label>Ordem</label>
                        <select value={order} onChange={(e) => setOrder(e.target.value)}>
                            <option value="oldest">Mais antigas primeiro</option>
                            <option value="newest">Mais recentes primeiro</option>
                        </select>
                    </div>
                    <div className="vr-filters-actions">
                        <button className="vr-btn vr-btn-primary" onClick={buscar} disabled={loading}>{loading ? 'Buscando…' : 'Buscar'}</button>
                    </div>
                </div>

                {feedbackIds && subdomain && (
                    <div className="vr-alert-filter">
                        Mostrando só os atendimentos do alerta de dislike da página inicial.
                        <button className="vr-btn vr-btn-sm" onClick={() => setFeedbackIds('')}>Ver todos</button>
                    </div>
                )}

                {subdomain ? (
                    <div className="vr-company">
                        <div className="vr-company-head" style={{ cursor: 'default' }}>
                            <span className="vr-company-name">{companyName(subdomain)}</span>
                            {Array.isArray(rowsBySd[subdomain]) && <span className="vr-count">{rowsBySd[subdomain].length}</span>}
                        </div>
                        <DisputeTable rows={rowsBySd[subdomain]} subdomain={subdomain} onOpen={openCase} />
                    </div>
                ) : (
                    <div className="vr-companies">
                        {overview === null && <div className="vr-empty">Carregando a fila…</div>}
                        {Array.isArray(overview) && overview.length === 0 && (
                            <div className="vr-card">Nenhuma reclamação pendente. 🎉</div>
                        )}
                        {(overview || []).map((c) => (
                            <div key={c.subdomain} className="vr-company">
                                <div className="vr-company-head" onClick={() => toggleCompany(c.subdomain)}>
                                    <span className={`vr-chevron ${open[c.subdomain] ? 'open' : ''}`}>›</span>
                                    <span className="vr-company-name">{c.name}</span>
                                    <span className="vr-company-meta">
                                        {c.pending_disputes ?? c.pending} VAR · {c.pending_dislikes ?? 0} 👎 feedback · mais antiga: {brDateTime(c.oldest_pending_at)}
                                    </span>
                                    <span className="vr-count" title="pendentes">{c.pending}</span>
                                </div>
                                {open[c.subdomain] && <DisputeTable rows={rowsBySd[c.subdomain]} subdomain={c.subdomain} onOpen={openCase} />}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default Reclamacoes;
